/**
 * use-ai.ts — Hook & kiểu dữ liệu cho nhóm trang "AI" (eCDP).
 * Bọc các endpoint backend dưới /api/v1/ai (base client đã có /api/v1).
 * Chỉ dùng lại `api` + TanStack Query + `useApiQuery` (không sửa file chung).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api-client'
import { FEATURES } from '@/lib/features'
import { useApiQuery } from '@/hooks/use-api'

// ── Query keys ──────────────────────────────────────────────────────
export const aiKeys = {
  config: ['ai', 'config'] as unknown[],
  usage: (days: number): unknown[] => ['ai', 'usage', days],
  replyRuns: (limit: number): unknown[] => ['ai', 'reply-runs', limit],
  trace: (runId: string | null): unknown[] => ['ai', 'trace', runId],
  scenarios: ['ai', 'scenarios'] as unknown[],
  knowledgeGaps: (status: string): unknown[] => ['ai', 'knowledge-gaps', status],
  channelOverrides: ['ai', 'channel-overrides'] as unknown[],
  customModels: ['ai', 'custom-models'] as unknown[],
  bots: ['ai', 'bots'] as unknown[],
}

// ── Kiểu dữ liệu ────────────────────────────────────────────────────
export interface ProviderModel {
  title: string
  value: string
}
export interface ProviderDef {
  id: string
  name: string
  baseUrl: string
  models: ProviderModel[]
  supportsCaching: boolean
  primary: boolean
}

export type AiMode = 'manual' | 'suggest' | 'auto'

export type TaskOverride = { provider?: string | null; model?: string | null }
export type TaskOverrides = Record<string, TaskOverride>

export interface AiConfig {
  orgId: string
  provider: string
  model: string
  maxDaily: number
  enabled: boolean
  taskOverrides?: TaskOverrides | null
  defaultAiMode: AiMode
  autoReplyEnabled: boolean
  debounceSeconds: number
  prefilterKeywords: string | null
  /** Nhóm: chỉ trả lời khi được nhắc tên. */
  groupRequireMention: boolean
  /** Tên gọi coi là được nhắc, cách nhau bằng dấu phẩy. */
  mentionNames: string | null
  verifyBeforeSend?: boolean
  visionEnabled?: boolean
  hasOpenaiKey: boolean
  hasMinimaxKey: boolean
  hasAnthropicKey: boolean
  hasGeminiKey: boolean
  availableProviders: ProviderDef[]
  isAfterHours: boolean
}

export interface AiUsage {
  callsToday: number
  tokensIn: number
  tokensOut: number
  cacheReadTokens: number
  costUsd: number
  maxDaily: number
  remaining: number
  enabled: boolean
  byType: Record<string, number>
  days: number
}

export interface ReplyRun {
  id: string
  conversationId: string | null
  mode: string | null
  status: string | null
  handoff: boolean | null
  confidence: number | null
  latencyMs: number | null
  createdAt: string
}

export interface AiTrace {
  id: string
  conversationId: string | null
  aiReplyRunId: string | null
  step: string
  level: string
  payload: unknown
  latencyMs: number | null
  createdAt: string
  expiresAt: string | null
}

export interface ScenarioMeta {
  id: string
  key: string
  name: string
  description: string
  loadMode: string
  enabled: boolean
  priority: number
  version: number
  updatedAt: string
}

export interface ScenarioDetail extends ScenarioMeta {
  content: string
  triggerHints: string | null
  updatedBy: string | null
  createdAt: string
}

export interface ScenarioInput {
  key?: string
  name: string
  description: string
  content: string
  loadMode?: string
  triggerHints?: string | null
  priority?: number
  enabled?: boolean
}

export type GapStatus = 'open' | 'resolved' | 'dismissed'

export interface KnowledgeGap {
  id: string
  conversationId: string | null
  contactId: string | null
  aiReplyRunId: string | null
  gapType: string
  question: string
  suggestion: string | null
  status: GapStatus
  occurrences: number
  resolvedBy: string | null
  notes: string | null
  lastSeenAt: string
  createdAt: string
  updatedAt: string
}

// Kênh (channel account) + override AI theo kênh
export interface ChannelInfo {
  id: string
  displayName: string | null
  platform: number
  status: string
}
export interface ChannelOverride {
  provider?: string
  model?: string
}
export type ChannelOverrides = Record<string, ChannelOverride>

// Model tuỳ chỉnh theo provider (catalog dạng dữ liệu — thêm không cần restart)
export type CustomModels = Record<string, ProviderModel[]>

// AI Bot ("con AI") — mỗi bot một bộ cấu hình riêng, gán theo kênh
export interface ToolConfigEntry {
  enabled: boolean
  /** search_products: id THƯ MỤC tài liệu bán hàng (gồm con); search_knowledge: id nhóm kiến thức. [] = tất cả. */
  guardrail: { categoryIds: string[] }
}
/** Công cụ hành động có thể gỡ khỏi bot (khớp CONG_CU_TAT_DUOC ở BE). */
export const CONG_CU_HANH_DONG = [
  { name: 'create_order', label: 'Lên đơn nháp', moTa: 'Tự tạo đơn hàng nháp khi khách chốt' },
  { name: 'lookup_order', label: 'Tra đơn hàng', moTa: 'Tra trạng thái đơn theo mã / SĐT' },
  { name: 'request_appointment', label: 'Đặt lịch hẹn', moTa: 'Ghi nhận yêu cầu hẹn lịch' },
  { name: 'send_product_image', label: 'Gửi ảnh sản phẩm', moTa: 'Gửi ảnh từ tài liệu bán hàng' },
  { name: 'send_document', label: 'Gửi tài liệu', moTa: 'Gửi bảng giá, chính sách dạng tệp' },
] as const
export type CongCuHanhDong = (typeof CONG_CU_HANH_DONG)[number]['name']

export interface BotToolsJson {
  search_products: ToolConfigEntry
  search_knowledge: ToolConfigEntry
  /** Công cụ hành động bị gỡ. Thiếu = không gỡ gì. */
  tatCongCu?: CongCuHanhDong[]
}

export type VaiTroBot = 'sales' | 'cskh' | 'orders' | 'internal'
export const NHAN_VAI_TRO: Record<VaiTroBot, string> = { sales: 'Bán hàng', cskh: 'CSKH', orders: 'Đơn hàng', internal: 'Nội bộ' }

export interface PersonaForm {
  tenXung: string
  giongDieu: 'than_mat' | 'trang_trong'
  luonLam: string[]
  cam: string[]
}

export interface EvalSummary { runId: string; passed: number; total: number; finishedAt: string | null }
/** Lượt chạy thật + token của bot (chỉ có ở GET /ai/bots). */
export interface UsageSummary {
  runs30d: number
  tokens30d: number
  costUsd30d: number
  runsAll: number
  tokensAll: number
  lastRunAt: string | null
  modelHieuLuc: { provider: string; model: string }
}

export interface AiBot {
  id: string
  name: string
  avatarEmoji: string | null
  description: string | null
  enabled: boolean
  provider: string | null
  model: string | null
  personaPrompt: string | null
  playbookPrompt: string | null
  handoffPrompt: string | null
  toolsJson: BotToolsJson | null
  channelAccountIds: string[]
  role: VaiTroBot | null
  /** null = mọi kịch bản org (trừ seed mẫu); [] = không; danh sách = đúng các id. */
  scenarioIds: string[] | null
  personaForm: PersonaForm | null
  /** Chỉ có ở GET /ai/bots (danh sách). */
  evalSummary?: EvalSummary | null
  usageSummary?: UsageSummary | null
  createdAt: string
  updatedAt: string
}
export type AiBotInput = Partial<Omit<AiBot, 'id' | 'createdAt' | 'updatedAt' | 'evalSummary' | 'usageSummary'>>

export interface BotTemplate {
  role: VaiTroBot
  name: string
  emoji: string
  moTa: string
  muctieu: string
  personaForm: PersonaForm
  tatCongCu: CongCuHanhDong[]
  traCuu: { sanPham: boolean; kienThuc: boolean }
  kichBan: Array<{ key: string; name: string }>
}

export interface TaoBotTuMauInput {
  role: VaiTroBot
  name?: string
  avatarEmoji?: string | null
  channelAccountIds?: string[]
  personaForm?: PersonaForm | null
  docFolderIds?: string[]
  knowledgeCategoryIds?: string[]
  scenarioIds?: string[] | null
}

// ── Query hooks ─────────────────────────────────────────────────────
export function useAiConfig() {
  return useApiQuery<AiConfig>(aiKeys.config, '/ai/config')
}

// Ba nhóm dưới đây backend TDVN chưa có → không gọi (UI đã ẩn theo cờ).
export function useChannelOverrides() {
  return useApiQuery<{ overrides: ChannelOverrides; channels: ChannelInfo[] }>(
    aiKeys.channelOverrides,
    '/ai/channel-overrides',
    undefined,
    { enabled: FEATURES.AI_CHANNEL_OVERRIDES },
  )
}

export function useCustomModels() {
  return useApiQuery<{ customModels: CustomModels }>(
    aiKeys.customModels,
    '/ai/custom-models',
    undefined,
    { enabled: FEATURES.AI_CUSTOM_MODELS },
  )
}

export function useAiBots() {
  return useApiQuery<{ bots: AiBot[] }>(aiKeys.bots, '/ai/bots', undefined, { enabled: FEATURES.AI_BOTS })
}

export function useBotTemplates() {
  return useApiQuery<{ templates: BotTemplate[] }>([...aiKeys.bots, 'templates'], '/ai/bots/templates', undefined, { enabled: FEATURES.AI_BOTS })
}

export function useAiUsage(days: number) {
  return useApiQuery<AiUsage>(aiKeys.usage(days), '/ai/usage', { days })
}

export function useReplyRuns(limit = 50) {
  return useApiQuery<{ runs: ReplyRun[] }>(aiKeys.replyRuns(limit), '/ai/reply-runs', { limit })
}

export function useAiScenarios() {
  return useApiQuery<{ scenarios: ScenarioMeta[] }>(aiKeys.scenarios, '/ai/scenarios')
}

export function useKnowledgeGaps(status: GapStatus) {
  return useApiQuery<{ data: KnowledgeGap[]; total: number }>(
    aiKeys.knowledgeGaps(status),
    '/ai/knowledge-gaps',
    { status },
  )
}

// ── Mutation helpers (gọi trực tiếp qua `api`) ──────────────────────
export async function saveApiKey(provider: string, apiKey: string, skipVerify?: boolean) {
  const { data } = await api.put('/ai/api-key', { provider, apiKey, skipVerify })
  return data as { ok: boolean; verified: boolean }
}

export async function deleteApiKey(provider: string) {
  const { data } = await api.delete(`/ai/api-key/${provider}`)
  return data as { ok: boolean }
}

export async function updateAiConfig(body: Partial<{
  provider: string
  model: string
  maxDaily: number
  enabled: boolean
  taskOverrides: TaskOverrides
  defaultAiMode: AiMode
  autoReplyEnabled: boolean
  debounceSeconds: number
  prefilterKeywords: string | null
  /** Nhóm: chỉ trả lời khi được nhắc tên. */
  groupRequireMention: boolean
  /** Tên gọi coi là được nhắc, cách nhau bằng dấu phẩy. */
  mentionNames: string | null
  verifyBeforeSend: boolean
  visionEnabled?: boolean
}>) {
  const { data } = await api.put('/ai/config', body)
  return data as AiConfig
}

export async function saveChannelOverrides(overrides: ChannelOverrides) {
  const { data } = await api.put('/ai/channel-overrides', { overrides })
  return data as { overrides: ChannelOverrides }
}

export async function saveCustomModels(customModels: CustomModels) {
  const { data } = await api.put('/ai/custom-models', { customModels })
  return data as { customModels: CustomModels }
}

export async function createAiBot(input: AiBotInput) {
  const { data } = await api.post('/ai/bots', input)
  return data as { bot: AiBot }
}

export async function updateAiBot(id: string, input: AiBotInput) {
  const { data } = await api.put(`/ai/bots/${id}`, input)
  return data as { bot: AiBot }
}

export async function deleteAiBot(id: string) {
  const { data } = await api.delete(`/ai/bots/${id}`)
  return data as { deleted: boolean }
}

export async function createBotFromTemplate(input: TaoBotTuMauInput) {
  const { data } = await api.post('/ai/bots/from-template', input)
  return data as { bot: AiBot }
}

export async function applyBotTemplate(id: string, role: VaiTroBot, giuPrompt = false) {
  const { data } = await api.post(`/ai/bots/${id}/apply-template`, { role, giuPrompt })
  return data as { bot: AiBot }
}

export type LoaiPrompt = 'persona' | 'playbook' | 'handoff_rules' | 'index' | 'mechanism' | 'criteria' | 'scenario'
export interface SoanPromptInput {
  loai: LoaiPrompt
  cheDo: 'tao_moi' | 'toi_uu'
  moTa?: string
  noiDungHienTai?: string
  botId?: string
  kichBan?: { name?: string; description?: string; triggerHints?: string }
}
/** AI viết / tối ưu một loại tài liệu train — chỉ trả nội dung, người dùng tự Lưu. */
export async function soanPromptBangAi(input: SoanPromptInput) {
  const { data } = await api.post('/ai/prompt-writer', input)
  return data as { content: string; kichBan?: { name: string; description: string; triggerHints: string }; model: string }
}

/** Xem trước persona biên từ form — biên ở BE để FE và bot dùng chung một bản. */
export async function previewPersona(form: PersonaForm, role: VaiTroBot) {
  const { data } = await api.post('/ai/bots/persona-preview', { form, role })
  return data as { personaPrompt: string }
}

// Demo chat cho bot: tạo hội thoại sandbox rồi mô phỏng trả lời với botId
export async function createSandboxConversation(name: string) {
  const { data } = await api.post('/ai/simulate/conversation', { name, aiMode: 'suggest' })
  return data as { conversationId: string }
}

export async function simulateBotReply(conversationId: string, customerText: string, botId: string, imageUrls: string[] = []) {
  const { data } = await api.post('/ai/simulate/reply', { conversationId, customerText, botId, includeTrace: false, imageUrls })
  return data as {
    reply: string | null
    runId?: string
    handoff?: { should: boolean; reason?: string } | null
    /** Lỗi hạ tầng — khác hẳn việc AI chủ động không trả lời. */
    error?: string | null
  }
}

export async function fetchScenario(id: string) {
  const { data } = await api.get(`/ai/scenarios/${id}`)
  return data as { scenario: ScenarioDetail }
}

export async function createScenario(input: ScenarioInput) {
  const { data } = await api.post('/ai/scenarios', input)
  return data as { scenario: ScenarioDetail }
}

export async function updateScenario(id: string, input: Partial<ScenarioInput>) {
  const { data } = await api.put(`/ai/scenarios/${id}`, input)
  return data as { scenario: ScenarioDetail }
}

export async function deleteScenario(id: string) {
  const { data } = await api.delete(`/ai/scenarios/${id}`)
  return data as { deleted: boolean }
}

export async function fetchTrace(runId: string) {
  const { data } = await api.get('/ai/trace', { params: { runId } })
  return data as { traces: AiTrace[]; nextCursor: string | null }
}

export async function dismissGap(id: string) {
  const { data } = await api.post(`/ai/knowledge-gaps/${id}/dismiss`)
  return data as { success: boolean }
}

export async function resolveGap(id: string, content: string, title?: string) {
  const { data } = await api.post(`/ai/knowledge-gaps/${id}/resolve`, { content, title })
  return data as { success: boolean; entryId?: string }
}

// ── Khung hướng dẫn (khối prompt của harness, ghi đè theo org/bot) ──────────

export type ViTriKhoi = 'mo_dau' | 'tai_lieu' | 'du_lieu' | 'tinh_huong' | 'cong_cu' | 'cuoi' | 'router' | 'critic' | 'xung_ho' | 'mo_ta_cong_cu' | 'nguon'

export interface KhoiHuongDan {
  key: string
  ten: string
  moTa: string
  loai: 'khoi' | 'nguon'
  viTri: ViTriKhoi
  cheDo: 'rag' | 'agent' | 'ca_hai' | 'router' | 'critic' | null
  thuTu: number
  khoa: boolean
  bien: string[]
  macDinh: string
  noiDung: string
  bat: boolean
  ghiDe: 'bot' | 'org' | null
  capNhatLuc: string | null
}

export const khoiKeys = {
  list: (botId?: string | null): unknown[] => ['ai', 'prompt-blocks', botId ?? ''],
  preview: (botId?: string | null): unknown[] => ['ai', 'prompt-blocks', 'preview', botId ?? ''],
}

export function useKhoiHuongDan(botId?: string | null) {
  return useApiQuery<{ khoi: KhoiHuongDan[] }>(khoiKeys.list(botId), '/ai/prompt-blocks', botId ? { botId } : undefined)
}

export function useXemPromptCuoi(botId: string | null | undefined, enabled: boolean) {
  return useQuery<{ rag: string; agent: string; router: string; ghiChu: string }>({
    queryKey: khoiKeys.preview(botId),
    queryFn: async () => {
      const { data } = await api.get('/ai/prompt-blocks/preview', { params: botId ? { botId } : {} })
      return data
    },
    enabled,
  })
}

export interface PhienBanKhoi {
  id: string
  content: string | null
  enabled: boolean
  updatedBy: string | null
  ghiChu: string | null
  createdAt: string
}

export function useLichSuKhoi(key: string | null, botId: string | null | undefined, enabled: boolean) {
  return useQuery<{ versions: PhienBanKhoi[] }>({
    queryKey: ['ai', 'prompt-blocks', 'versions', key ?? '', botId ?? ''],
    queryFn: async () => {
      const { data } = await api.get(`/ai/prompt-blocks/${encodeURIComponent(key ?? '')}/versions`, { params: botId ? { botId } : {} })
      return data
    },
    enabled: enabled && !!key,
  })
}

export function useGhiKhoi() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { key: string; botId?: string | null; content?: string | null; enabled?: boolean }) => {
      const { data } = await api.put(`/ai/prompt-blocks/${encodeURIComponent(input.key)}`, {
        botId: input.botId ?? null, content: input.content, enabled: input.enabled,
      })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai', 'prompt-blocks'] }),
  })
}

export function useKhoiPhucKhoi() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: { key: string; botId?: string | null }) => {
      const { data } = await api.delete(`/ai/prompt-blocks/${encodeURIComponent(input.key)}`, { params: input.botId ? { botId: input.botId } : {} })
      return data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai', 'prompt-blocks'] }),
  })
}
